import {
  CATALOG_ROUTES,
  catalogListResponseSchema,
  catalogOptionResponseSchema,
  type CatalogKind,
  type CatalogList,
  type CatalogOption,
  type CatalogOptionInput,
  type CatalogOptionStatus,
  type CatalogOptionUpdate,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";

/**
 * Las listas de opciones —categorías y tipos de ítem— vistas desde el
 * panel.
 *
 * Todas las llamadas pasan por `authFetch`: adjunta el access token,
 * renueva una vez si expiró y cierra la sesión si el token es inválido.
 * Ninguna pantalla toca `fetch` a mano.
 *
 * `baseUrl: ""` porque `CATALOG_ROUTES` YA trae el prefijo `/api`. Sin
 * esto quedaría `/api/api/admin/catalog-options` y el 404 se descubriría
 * en ejecución, no al compilar.
 */
const NO_PREFIX = { baseUrl: "" } as const;

export interface CatalogQuery {
  kind?: CatalogKind;
  status?: CatalogOptionStatus;
}

/**
 * La URL con sus filtros, con el orden de los parámetros FIJO.
 *
 * Así dos estados idénticos producen la misma URL, react-query los
 * reconoce como la misma entrada de caché y las pruebas pueden responder
 * por ruta exacta.
 */
export function catalogOptionsUrl(query: CatalogQuery): string {
  const params = new URLSearchParams();
  if (query.kind) params.set("kind", query.kind);
  if (query.status) params.set("status", query.status);
  const qs = params.toString();
  return qs ? `${CATALOG_ROUTES.adminList}?${qs}` : CATALOG_ROUTES.adminList;
}

export function listCatalogOptions(query: CatalogQuery): Promise<CatalogList> {
  return authFetch(catalogOptionsUrl(query), catalogListResponseSchema, NO_PREFIX);
}

export async function createCatalogOption(
  input: CatalogOptionInput,
): Promise<CatalogOption> {
  const data = await authFetch(CATALOG_ROUTES.adminCreate, catalogOptionResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
  return data.option;
}

export async function updateCatalogOption(
  id: string,
  patch: CatalogOptionUpdate,
): Promise<CatalogOption> {
  const data = await authFetch(CATALOG_ROUTES.adminOne(id), catalogOptionResponseSchema, {
    ...NO_PREFIX,
    method: "PATCH",
    body: patch,
  });
  return data.option;
}
